import React, { useState, useEffect } from "react";
import api from "../services/api";
import { setToken } from "../services/auth";
import { Link, useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  LogIn,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Smartphone,
  KeyRound,
  Globe,
  Building2,
  MessageCircle,
  Hash,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import { GoogleLogin } from '@react-oauth/google';
import illustration from "../assets/learning_illustration.png";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

export default function Login() {
  const navigate = useNavigate();
  // Google OAuth only works on plain localhost or real TLD domains (not *.localhost subdomains)
  const hostname = window.location.hostname;
  const isSubdomainLocalhost = hostname.endsWith('.localhost') || (hostname.endsWith('localhost') && hostname !== 'localhost');
  const showSocialLogin = !isSubdomainLocalhost;
  const isSubdomain = hostname !== "localhost" && hostname !== "skillzy.pro" && !/^[0-9.]+$/.test(hostname);
  const [loading, setLoading] = useState(false);

  // Forgot Password State
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Mobile Login State
  const [mobileNo, setMobileNo] = useState("");
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileOtp, setMobileOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  // Role Selection State
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [selectedRole, setSelectedRole] = useState("student");

  useEffect(() => {
    const handleAuthMessage = async (event) => {
      if (event.data?.type === "firebase_auth_success") {
        const firebaseToken = event.data.token;
        setLoading(true);
        try {
          const res = await api.post("/auth/mobile/login", {
            firebase_token: firebaseToken
          });
          if (res.data.error) throw new Error(res.data.error);

          setToken(res.data.access_token);

          if (res.data.is_new_user) {
            setShowRoleSelection(true);
            toast.success("Account created successfully!");
            return;
          }

          toast.success('Welcome back!');

          const { jwtDecode: decode } = await import("jwt-decode");
          const decoded = decode(res.data.access_token);
          const dest = decoded.role === "admin" ? "/admin" :
            decoded.role === "institute" ? "/institute" :
              decoded.role === "parent" ? "/parent-dashboard" : "/home";
          setTimeout(() => navigate(dest), 1000);
        } catch (err) {
          toast.error(err.response?.data?.detail || err.message || "Invalid OTP");
        } finally {
          setLoading(false);
        }
      }
    };

    window.addEventListener("message", handleAuthMessage);
    return () => window.removeEventListener("message", handleAuthMessage);
  }, [navigate]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/google", {
        credential: credentialResponse.credential
      });

      if (res.data.error) throw new Error(res.data.error);

      setToken(res.data.access_token);

      if (res.data.is_new_user) {
        setShowRoleSelection(true);
        toast.success("Account created successfully!");
        return;
      }

      toast.success('Welcome back with Google!');

      const { jwtDecode: decode } = await import("jwt-decode");
      const decoded = decode(res.data.access_token);
      const dest = decoded.role === "admin" ? "/admin" :
        decoded.role === "institute" ? "/institute" :
          decoded.role === "parent" ? "/parent-dashboard" : "/home";
      setTimeout(() => navigate(dest), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Google Login failed');
    } finally {
      setLoading(false);
    }
  };

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
      });
    }
  };

  const handleMobileSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSubdomain) {
        const isLocalhost = hostname === "localhost" || hostname.endsWith(".localhost");
        const rootAuthDomain = isLocalhost ? "http://localhost:3000" : "https://skillzy.pro";
        const targetUrl = `${rootAuthDomain}/auth-helper?phone=${encodeURIComponent(mobileNo)}&origin=${encodeURIComponent(window.location.origin)}`;

        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(targetUrl, "firebase_auth_popup", `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes,scrollbars=yes`);
        toast.success("Opening secure verification popup...");
        setLoading(false);
        return;
      }

      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      const formattedNo = mobileNo.startsWith('+') ? mobileNo : `+91${mobileNo}`;

      const confirmation = await signInWithPhoneNumber(auth, formattedNo, appVerifier);
      setConfirmationResult(confirmation);
      setMobileOtpSent(true);
      toast.success("OTP sent to your mobile!");
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleMobileLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await confirmationResult.confirm(mobileOtp);
      const firebaseToken = await result.user.getIdToken();

      const res = await api.post("/auth/mobile/login", {
        firebase_token: firebaseToken
      });

      if (res.data.error) throw new Error(res.data.error);

      setToken(res.data.access_token);

      if (res.data.is_new_user) {
        setShowRoleSelection(true);
        toast.success("Account created successfully!");
        return;
      }

      toast.success('Welcome back!');

      const { jwtDecode: decode } = await import("jwt-decode");
      const decoded = decode(res.data.access_token);
      const dest = decoded.role === "admin" ? "/admin" :
        decoded.role === "institute" ? "/institute" :
          decoded.role === "parent" ? "/parent-dashboard" : "/home";
      setTimeout(() => navigate(dest), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSubmit = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/set-role", { role: selectedRole });
      setToken(res.data.access_token);
      toast.success(`Role updated to ${selectedRole}!`);

      const dest = selectedRole === "parent" ? "/parent-dashboard" : "/home";
      setTimeout(() => navigate(dest), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set role");
    } finally {
      setLoading(false);
    }
  };

  const handleSendForgotOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password/send-otp", { identifier });
      toast.success("OTP sent successfully!");
      if (res.data.otp) alert(`Development Mode: Reset OTP is ${res.data.otp}`);
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "User not found");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", { identifier, otp, new_password: newPassword });
      toast.success("Password reset successful!");
      setShowForgot(false);
      setForgotStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      <Link to="/" className="absolute top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-white hover:text-indigo-600 transition-all shadow-sm">
        <ArrowLeft size={18} /> Back to Home
      </Link>

      {/* Background Gradients */}
      <div className="fixed inset-0 bg-[#eef2ff] -z-10" />
      <div className="fixed top-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#dbeafe] rounded-full blur-[120px] opacity-60 animate-pulse" />
      <div className="fixed bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#ede9fe] rounded-full blur-[120px] opacity-60 animate-pulse" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-6xl z-10"
      >
        <div className="bg-white/40 backdrop-blur-3xl rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] border border-white/40 overflow-hidden flex flex-col lg:flex-row min-h-[700px]">

          {/* Left Column: Illustration */}
          <div className="w-full lg:w-1/2 p-12 lg:p-16 flex flex-col items-center justify-center bg-gradient-to-br from-white/20 to-transparent border-r border-white/20">
            <motion.div
              whileHover={{ y: -10 }}
              className="w-full max-w-sm drop-shadow-2xl"
            >
              <img src={illustration} alt="Illustration" className="w-full h-auto rounded-3xl" />
            </motion.div>
            <div className="mt-12 text-center hidden lg:block">
              <h2 className="text-2xl font-black text-slate-800">Your Future Starts Here</h2>
              <p className="text-slate-600 mt-3 max-w-xs mx-auto text-sm font-medium">
                Log in to access your dashboard, courses, and more.
              </p>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="w-full lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center">
            <div className="max-w-md mx-auto w-full">
              <AnimatePresence mode="wait">
                {showRoleSelection ? (
                  <motion.div
                    key="role-select"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <div className="mb-10 text-center lg:text-left">
                      <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-3">Welcome to Academy Pro</h1>
                      <p className="text-slate-500 text-[15px] font-medium">
                        Please tell us who you are to personalize your experience.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                      {["student", "teacher", "parent"].map(role => (
                        <button
                          key={role}
                          onClick={() => setSelectedRole(role)}
                          className={`flex-1 p-4 rounded-2xl border-2 flex items-center justify-center gap-3 transition-all ${selectedRole === role
                            ? 'border-indigo-600 bg-indigo-50/50 shadow-sm'
                            : 'border-slate-200 hover:border-indigo-300 bg-white/40'
                            }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedRole === role ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                            }`}>
                            {selectedRole === role && <Check size={12} strokeWidth={4} className="text-white" />}
                          </div>
                          <span className="font-bold text-slate-700 capitalize text-sm whitespace-nowrap">I'm a {role}</span>
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={handleRoleSubmit}
                      disabled={loading}
                      className="w-full py-4.5 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white font-bold rounded-2xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98] disabled:opacity-70"
                    >
                      {loading ? "Saving..." : "Continue"}
                    </button>
                  </motion.div>
                ) : !showForgot ? (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <div className="mb-10 text-center lg:text-left">
                      <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-3">Welcome Back</h1>
                      <p className="text-slate-500 text-[15px] font-medium">
                        Please enter your details to sign in to your account.
                      </p>
                    </div>




                    <form onSubmit={mobileOtpSent ? handleMobileLogin : handleMobileSendOtp} className="space-y-5">
                      <input
                        type="tel"
                        required
                        placeholder="Mobile Number (e.g. +91...)"
                        disabled={mobileOtpSent}
                        className="w-full px-5 py-4 bg-white/60 border-0 rounded-2xl focus:ring-2 focus:ring-indigo-500/50 outline-none text-slate-800 shadow-sm placeholder:text-slate-400 transition-all disabled:opacity-60"
                        value={mobileNo}
                        onChange={(e) => setMobileNo(e.target.value)}
                      />

                      {mobileOtpSent && (
                        <motion.input
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          type="text"
                          required
                          placeholder="Enter 6-digit OTP"
                          className="w-full px-5 py-4 bg-white/60 border-0 rounded-2xl focus:ring-2 focus:ring-indigo-500/50 outline-none text-slate-800 shadow-sm placeholder:text-slate-400 transition-all"
                          value={mobileOtp}
                          onChange={(e) => setMobileOtp(e.target.value)}
                          maxLength={6}
                        />
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4.5 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white font-bold rounded-2xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98] disabled:opacity-70 mt-2"
                      >
                        {loading ? "Processing..." : (mobileOtpSent ? "Verify & Login" : "Send OTP")}
                      </button>
                    </form>
                    <div id="recaptcha-container"></div>


                    <div className="mt-8 text-center space-y-4">
                      <p className="text-slate-500 text-sm">
                        Don't have an account?{" "}
                        <Link to="/register" className="text-[#ef4444] font-bold hover:underline">
                          Sign Up here
                        </Link>
                      </p>
                      {!isSubdomain && (
                        <div className="border-t border-slate-200/50 pt-4">
                          <Link
                            to="/register-institute"
                            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100/80 px-4 py-2.5 rounded-xl transition-all border border-indigo-100/50"
                          >
                            <Building2 size={13} /> Educators: Launch Your SaaS LMS in 1-Click
                          </Link>
                        </div>
                      )}

                      {showSocialLogin && (
                        <>
                          <div className="relative my-8">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-transparent px-2 text-slate-400 font-bold">OR Continue With</span>
                            </div>
                          </div>

                          <div className="flex flex-col items-center justify-center gap-4">
                            <div className="w-full flex justify-center">
                              <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => toast.error('Google Login Failed')}
                                theme="outline"
                                shape="pill"
                                size="large"
                              />
                            </div>

                          </div>
                        </>
                      )}

                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="forgot"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="mb-10 text-center lg:text-left">
                      <div className="inline-flex p-4 rounded-2xl bg-amber-600/10 mb-4 text-amber-600 shadow-inner">
                        <KeyRound size={28} />
                      </div>
                      <h1 className="text-3xl font-black text-slate-900 tracking-tight">Reset Password</h1>
                      <p className="text-slate-500 mt-2 text-sm font-medium">
                        {forgotStep === 1 ? "Enter email or phone number to receive OTP" : "Verify OTP and set a new password"}
                      </p>
                    </div>

                    {forgotStep === 1 ? (
                      <form onSubmit={handleSendForgotOtp} className="space-y-5">
                        <input
                          type="text"
                          required
                          placeholder="Email or Mobile"
                          className="w-full px-5 py-4 bg-white/60 border-0 rounded-2xl focus:ring-2 focus:ring-amber-500/50 outline-none text-slate-800 shadow-sm transition-all"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full py-4.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-2xl shadow-lg shadow-amber-200 transition-all active:scale-[0.98]"
                        >
                          {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : "Send OTP"}
                        </button>
                        <button type="button" onClick={() => setShowForgot(false)} className="w-full text-slate-400 text-sm font-bold hover:text-slate-600">Back to Login</button>
                      </form>
                    ) : (
                      <form onSubmit={handleResetPassword} className="space-y-5">
                        <input
                          type="text"
                          required
                          placeholder="6-digit OTP"
                          className="w-full px-5 py-4 bg-white/60 border-0 rounded-2xl focus:ring-2 focus:ring-amber-500/50 outline-none text-slate-800 text-center text-xl tracking-[0.5em] font-black"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                        />
                        <input
                          type="password"
                          required
                          placeholder="New Password"
                          className="w-full px-5 py-4 bg-white/60 border-0 rounded-2xl focus:ring-2 focus:ring-amber-500/50 outline-none text-slate-800"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full py-4.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl shadow-lg shadow-green-200 transition-all active:scale-[0.98]"
                        >
                          {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : "Reset Password"}
                        </button>
                        <button type="button" onClick={() => setForgotStep(1)} className="w-full text-slate-400 text-sm font-bold hover:text-slate-600">Back</button>
                      </form>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}